/**
 * useBundleEditor
 * ---------------
 * VolumeDiscount-flavored adapter around the shared, schema-agnostic
 * useVariantEditor engine (src/hooks/useVariantEditor.ts) — supplies the
 * bundle Zod resolver and SKIP_KEYS, everything else (variant cache, A/B
 * enable/add/remove, payload/override building) lives in the shared engine
 * so future bundle types can reuse it the same way.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import type { Resolver } from "react-hook-form";
import { bundleSchema } from "./bundle.schema";
import type { VolumeConfigInterface } from "../../Types";
import { useVariantEditor, type UseVariantEditorReturn } from "../../../../hooks/useVariantEditor";

export { BASE_TARGET } from "../../../../hooks/useVariantEditor";
export type { EditorVariant } from "../../../../hooks/useVariantEditor";

/** Keys that are ALWAYS taken from base, regardless of which variant is active. */
export const SKIP_KEYS: (keyof VolumeConfigInterface)[] = [
    "offer_name",
    "products",
    "volume_product_selection",
    "bundle_visibility",
    "collections",
    "bundle_product_level",
    "start_date",
    "end_date",
    "ab_config",
    "general_settings",
    "bundles_settings",
    "show_bundle_widget",
    "free_shipping",
    "animation_add_to_cart_btn",
    "order_discounts",
    "product_discounts",
    "shipping_discounts",
    "targeting",
    "swatchOptions",
    "hide_native_elements",
];

export interface UseBundleEditorOptions {
    initialValues: VolumeConfigInterface;
    /**
     * Called when the user clicks "Save".
     * Receives the complete payload ready to send to the backend.
     */
    onSave: (payload: VolumeConfigInterface) => Promise<void>;
}

export function useBundleEditor({ initialValues, onSave }: UseBundleEditorOptions) {
    return useVariantEditor<VolumeConfigInterface>({
        initialValues,
        resolver: zodResolver(bundleSchema) as unknown as Resolver<VolumeConfigInterface>,
        skipKeys: SKIP_KEYS,
        onSave,
    });
}

export type UseBundleEditorReturn = UseVariantEditorReturn<VolumeConfigInterface>;
